import { z } from 'zod';

export const patientSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  phone: z.string().optional(),
  date_of_birth: z.string().optional(),
  blood_group: z.string().optional(),
  gender: z.enum(['male', 'female', '']).optional(),
  note: z.string().optional(),
  address: z.string().optional(),
  emergency_contact: z.string().optional(),
  medical_history: z.string().optional(),
});

export const doctorSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Invalid email'),
  phone: z.string().optional(),
  specialization: z.string().min(1, 'Specialization is required'),
  department: z.string().optional(),
  license_number: z.string().optional(),
});

export const appointmentSchema = z.object({
  patient_id: z.string().min(1, 'Patient is required'),
  doctor_id: z.string().min(1, 'Doctor is required'),
  appointment_date: z.string().min(1, 'Date is required'),
  start_time: z.string().min(1, 'Start time is required'),
  end_time: z.string().min(1, 'End time is required'),
  status: z.enum(['scheduled', 'confirmed', 'completed', 'cancelled']),
  notes: z.string().optional(),
  referral_by: z.string().optional(),
  appointment_charge: z.number().min(0, 'Charge must be positive').default(0),
  payment_type: z.enum(['cash', 'card', 'bank_transfer', 'insurance']).optional(),
  payment_status: z.enum(['paid', 'unpaid', 'partial']).default('unpaid'),
  prescription_status: z.enum(['none', 'created', 'issued']).default('none'),
  department: z.string().optional(),
  contact_number: z.string().optional(),
});

export const invoiceSchema = z.object({
  patient_id: z.string().min(1, 'Patient is required'),
  due_date: z.string().optional(),
  tax_rate: z.number().min(0).max(100).optional(),
  notes: z.string().optional(),
});

export const invoiceItemSchema = z.object({
  description: z.string().min(1, 'Description is required'),
  quantity: z.number().min(1, 'Quantity must be at least 1'),
  unit_price: z.number().min(0, 'Price must be positive'),
});

export const paymentSchema = z.object({
  invoice_id: z.string().min(1, 'Invoice is required'),
  amount: z.number().min(0.01, 'Amount must be positive'),
  payment_date: z.string().min(1, 'Payment date is required'),
  payment_method: z.enum(['cash', 'card', 'bank_transfer', 'insurance']),
  reference_number: z.string().optional(),
  notes: z.string().optional(),
});

export const loginSchema = z.object({
  email: z.string().email('Invalid email'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

export const productSchema = z.object({
  name: z.string().min(1, 'Product name is required'),
  generic_name: z.string().optional(),
  category: z.string().min(1, 'Category is required'),
  unit: z.string().min(1, 'Unit is required'),
  price: z.number().min(0, 'Price must be positive'),
});

export const salesInvoiceSchema = z.object({
  customer_name: z.string().optional(),
  customer_phone: z.string().optional(),
  payment_method: z.enum(['cash', 'card']),
});

export const dispenseSchema = z.object({
  medication_order_id: z.string().min(1, 'Medication order is required'),
  quantity: z.number().min(1, 'Quantity must be at least 1'),
  batch_id: z.string().min(1, 'Batch selection is required'),
});

export const doctorIdentitySchema = z.object({
  name: z.string().min(1, 'Full name is required'),
  email: z.string().email('Invalid email address'),
  phone: z.string().regex(/^\+[1-9]\d{1,14}$/, 'Phone must be in E.164 format (e.g., +1234567890)'),
  gender: z.enum(['Male', 'Female', 'Other']).optional(),
  date_of_birth: z.string().optional(),
});

export const doctorCredentialsSchema = z.object({
  license_no: z.string().min(1, 'License number is required'),
  license_council: z.string().min(1, 'License council is required'),
  license_expiry: z.string().min(1, 'License expiry is required').refine(
    (date) => new Date(date) > new Date(),
    'License expiry must be a future date'
  ),
  degrees: z.array(z.string()).min(1, 'At least one degree is required'),
  specialization: z.string().min(1, 'Specialization is required'),
  subspecialties: z.array(z.string()).default([]),
});

export const doctorEmploymentSchema = z.object({
  department: z.string().min(1, 'Department is required'),
  designation: z.string().optional(),
  employment_type: z.enum(['full_time', 'part_time', 'visiting', 'locum']),
  joining_date: z.string().min(1, 'Joining date is required'),
  privileges: z.array(z.string()).default([]),
});

export const doctorScheduleSlotSchema = z.object({
  day_of_week: z.number().min(0).max(6),
  start_time: z.string().regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format'),
  end_time: z.string().regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format'),
  max_appointments: z.number().min(1).default(10),
});

export const doctorScheduleUploadsSchema = z.object({
  schedule: z.array(doctorScheduleSlotSchema).default([]),
});

export const enhancedDoctorSchema = doctorIdentitySchema
  .merge(doctorCredentialsSchema)
  .merge(doctorEmploymentSchema)
  .merge(doctorScheduleUploadsSchema);

export type PatientFormData = z.infer<typeof patientSchema>;
export type DoctorFormData = z.infer<typeof doctorSchema>;
export type DoctorIdentityFormData = z.infer<typeof doctorIdentitySchema>;
export type DoctorCredentialsFormData = z.infer<typeof doctorCredentialsSchema>;
export type DoctorEmploymentFormData = z.infer<typeof doctorEmploymentSchema>;
export type DoctorScheduleUploadsFormData = z.infer<typeof doctorScheduleUploadsSchema>;
export type EnhancedDoctorFormData = z.infer<typeof enhancedDoctorSchema>;
export type AppointmentFormData = z.infer<typeof appointmentSchema>;
export type InvoiceFormData = z.infer<typeof invoiceSchema>;
export type InvoiceItemFormData = z.infer<typeof invoiceItemSchema>;
export type PaymentFormData = z.infer<typeof paymentSchema>;
export type LoginFormData = z.infer<typeof loginSchema>;
export type ProductFormData = z.infer<typeof productSchema>;
export type SalesInvoiceFormData = z.infer<typeof salesInvoiceSchema>;
export type DispenseFormData = z.infer<typeof dispenseSchema>;
